import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { logout } from '../../../../../api'
import { PROFILE_ROUTE_NAMES } from '../../../../../constants/route'
import { LOCAL_STORAGE_NAME_TOKEN } from '../../../../../constants/storage'
import { useInitActions, useInitData } from '../../../../../hooks'
import { destroyProductFruits } from '../../../../../utils/productFruits'
import { removeStorageItem, setStorageItem } from '../../../../../utils/storage'
import { getSharedRequest } from '../../SharedRequest.api'

/**
 * Exchanges the email hash for a partner token and lands on the redesign
 * request profile. Returns `{ isValidHash, loading }` for the surrounding
 * `Auth` panel — mirrors `useAuthValidateHash`.
 */
export function useSharedRequestExchange() {
  const { hash } = useParams()

  const { resetApp } = useInitActions()
  const navigate = useNavigate()

  const { authorized } = useInitData()

  const [loading, setLoading] = useState(true)
  const [isValidHash, setValidHash] = useState(true)

  const exchangedHashRef = useRef(null)

  // Tear down any active session first (e.g. an internal user opening a partner
  // link) so the exchange runs unauthenticated and an invalid hash can surface
  // the error panel — `Auth` renders a loader while `authorized`. The
  // unauthenticated remount then runs the exchange below.
  useEffect(() => {
    if (authorized) {
      logout()
        .catch(error => console.warn('Shared request logout failed:', error))
        .finally(() => {
          destroyProductFruits()
          removeStorageItem(LOCAL_STORAGE_NAME_TOKEN)
          resetApp()
        })
    }
  }, [authorized])

  useEffect(() => {
    if (!authorized && exchangedHashRef.current !== hash) {
      exchangedHashRef.current = hash

      setLoading(true)
      setValidHash(true)

      getSharedRequest({ hash })
        .then(({ data }) => {
          const token = data?.user_token
          const requestId = data?.request?.id

          if (token && requestId) {
            // Order matters: store the token and navigate to the profile FIRST
            // (while the router is still mounted), then clear init/user data so
            // `InitProvider` re-fetches them with the new token and the rebuilt
            // router boots straight into the request profile.
            setStorageItem(LOCAL_STORAGE_NAME_TOKEN, token)
            navigate(PROFILE_ROUTE_NAMES.requestProfileNG({ id: requestId }), {
              replace: true,
            })
            resetApp()
          } else {
            setValidHash(false)
            setLoading(false)
          }
        })
        .catch(error => {
          console.warn('Shared request is invalid:', error)
          setValidHash(false)
          setLoading(false)
        })
    }
  }, [authorized, hash])

  return { isValidHash, loading }
}
